import type { Obstacle } from '../../types/game.types';
import { BackgroundScroller } from './Background';
import Tree00 from '../../assets/Tree00.png';
import Tree01 from '../../assets/Tree01.png';
import Rock00 from '../../assets/Rock00.png';
import { getSlopeBounds, obstacleSize } from './SlopeConfig';
import { generateObstacleSpecs, SpawnSpec } from './SeededPath';

export function computeScrollDistance(elapsedSeconds: number): number {
  const STEP = 0.05, FPS = STEP * 60;
  let dist = 0;
  for (let t = 0; t < elapsedSeconds; t += STEP)
    dist += raceScrollSpeedAtElapsed(t) * FPS;
  return dist;
}

export function raceScrollSpeedForMultiplier(multiplier: number): number {
  return Math.min(5 + multiplier * 0.45, 25);
}

function raceScrollSpeedAtElapsed(t: number): number {
  return raceScrollSpeedForMultiplier(Math.pow(1.0024, t * 60));
}


const createImg = (src: string) => { const i = new Image(); i.src = src; return i; };
const assetPools = { tree: [createImg(Tree00), createImg(Tree01)], rock: [createImg(Rock00)] };
function getAsset(type: 'tree'|'rock', v: number) { return assetPools[type]?.[v % assetPools[type].length] ?? new Image(); }

var isPaused = false;

interface SkiObstacle extends Obstacle {
  type:             'tree'|'rock';
  scale:            number;
  variant:          number;
  asset:            HTMLImageElement;
  spawnScrollTotal: number;
  spawnCanvasY:     number;
}

export class ObstacleManager {
  private obstacles:    SkiObstacle[] = [];
  private canvasWidth:  number;
  private canvasHeight: number;
  private background:   BackgroundScroller;

  private spawnTimer:    number = 0;
  private spawnInterval: number = 90;
  private obstacleSpeed: number = 3;

  private spawnSpecs:   SpawnSpec[] = [];
  private spawnSpecIdx: number      = 0;

  constructor(canvasWidth: number, canvasHeight: number, background: BackgroundScroller) {
    this.canvasWidth  = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.background   = background;
  }

  initSeed(serverSeedHash: string) {
    this.spawnSpecs   = generateObstacleSpecs(serverSeedHash);
    this.spawnSpecIdx = 0;
    this.spawnTimer   = 0;
    this.obstacles    = [];
  }


  reconstituteAtElapsed(elapsedSeconds: number) {
    if (this.spawnSpecs.length === 0) return;

    this.obstacles = [];
    const len      = this.spawnSpecs.length;
    const scrollNow = computeScrollDistance(elapsedSeconds);
    this.background.setScrollTotal(scrollNow);

    const { left, right } = getSlopeBounds(this.canvasWidth);

    let idx      = 0;
    let timer    = 0;
    let t        = 0;          
    const dt     = 1 / 60;    
    const totalFrames = Math.floor(elapsedSeconds * 60);

    let scrollAtFrame = 0;      

    for (let f = 0; f < totalFrames; f++) {
      t = f * dt;

      scrollAtFrame += raceScrollSpeedAtElapsed(t);

      timer++;
      if (timer >= this.spawnSpecs[idx % len].intervalTicks) {

        const spec = this.spawnSpecs[idx % len];
        idx++; timer = 0;

        const isTree = spec.isTree;
        const normX  = spec.normX;
        // Sized from the background's own scale so trees match the painted edge trees.
        const { width: w, height: h, shadowScale: scale } = obstacleSize(this.canvasWidth, isTree, spec.scale);
        const x = left + normX * (right - left - w);
        const type    = isTree ? 'tree' : 'rock' as const;
        const variant = isTree ? (normX > 0.5 ? 0 : 1) : 0;
        const spawnCanvasY    = this.canvasHeight + h + 20;
        const spawnScrollTotal = scrollAtFrame;

        const currentY = spawnCanvasY - (scrollNow - spawnScrollTotal);

        if (currentY < -h - 20) continue;

        this.obstacles.push({
          id:              `obs_${f}_${type}`,
          x,
          y:               currentY,
          width:           w,
          height:          h,
          speed:           this.obstacleSpeed,
          type,
          scale,
          variant,
          asset:           getAsset(type, variant),
          spawnScrollTotal,
          spawnCanvasY,
        });
      }
    }

    this.spawnSpecIdx = idx;
    this.spawnTimer   = timer;
  }


  update(dt = 16.667) {
    if (isPaused) return;

    this.spawnTimer += dt / 16.667;
    const interval = this.spawnSpecs.length > 0
      ? this.spawnSpecs[this.spawnSpecIdx % this.spawnSpecs.length].intervalTicks
      : this.spawnInterval;

    if (this.spawnTimer >= interval) { this.spawn(); this.spawnTimer -= interval; }

    const scrollNow = this.background.getScrollTotal();
    for (const obs of this.obstacles) obs.y = obs.spawnCanvasY - (scrollNow - obs.spawnScrollTotal);
    this.obstacles = this.obstacles.filter(obs => obs.y > -obs.height - 20);
  }

  spawn() {
    const { left, right } = getSlopeBounds(this.canvasWidth);
    let isTree: boolean, scale: number, normX: number;

    if (this.spawnSpecs.length > 0) {
      const s = this.spawnSpecs[this.spawnSpecIdx % this.spawnSpecs.length];
      this.spawnSpecIdx++; isTree=s.isTree; scale=s.scale; normX=s.normX;
    } else {
      isTree=Math.random()>0.30; scale=1.20+Math.random()*0.40; normX=Math.random();
    }
    const size=obstacleSize(this.canvasWidth,isTree,scale);
    const w=size.width, h=size.height;
    scale=size.shadowScale;
    const x=left+normX*(right-left-w);
    const type=isTree?'tree':'rock' as const, variant=isTree?(normX>0.5?0:1):0;
    const spawnCanvasY=this.canvasHeight+h+20;

    this.obstacles.push({
      id:`obs_${this.spawnSpecIdx}_${type}`, x, y:spawnCanvasY,
      width:w, height:h, speed:this.obstacleSpeed, type, scale, variant,
      asset:getAsset(type,variant),
      spawnScrollTotal:this.background.getScrollTotal(), spawnCanvasY,
    });
  }


  draw(ctx: CanvasRenderingContext2D) {
    [...this.obstacles].sort((a,b)=>a.y-b.y).forEach(obs=>
      obs.type==='tree'?this.drawTree(ctx,obs):this.drawRock(ctx,obs));
  }

  private drawTree(ctx: CanvasRenderingContext2D, obs: SkiObstacle) {
    ctx.save();
    ctx.fillStyle='rgba(0,50,0,0.5)'; ctx.beginPath();
    ctx.ellipse(obs.x+obs.width/2,obs.y+obs.height-5,20*obs.scale,8*obs.scale,0,0,Math.PI*2); ctx.fill();
    if (obs.asset) ctx.drawImage(obs.asset,obs.x,obs.y,obs.width,obs.height);
    ctx.restore();
  }

  private drawRock(ctx: CanvasRenderingContext2D, obs: SkiObstacle) {
    ctx.save();
    ctx.fillStyle='rgba(50,50,50,0.5)'; ctx.beginPath();
    ctx.ellipse(obs.x+obs.width/2,obs.y+obs.height-5,18*obs.scale,6*obs.scale,0,0,Math.PI*2); ctx.fill();
    if (obs.asset) ctx.drawImage(obs.asset,obs.x,obs.y,obs.width,obs.height);
    ctx.restore();
  }

  setDifficulty(m: number) { this.spawnInterval=Math.max(25,90-m*6); this.obstacleSpeed=Math.min(7,3+m*0.25); }

  // ── Crash helpers ─────────────────────────────────────────────────────────

  findCrashCandidate(sx: number, sy: number, maxDX=120, maxDY=110): Obstacle|null {
    const c=this.obstacles.filter(o=>Math.abs(o.x-sx)<=maxDX&&o.y<sy+maxDY&&o.y>sy-20);
    return c.length===0?null:c.reduce((a,b)=>Math.hypot(b.x-sx,b.y-sy)<Math.hypot(a.x-sx,a.y-sy)?b:a);
  }

  private _fixedObs(x:number,y:number,w:number,h:number,type:'tree'|'rock',scale=1.0): SkiObstacle {
    return { id:`crash_${type}_${Date.now()}`, x, y, width:w, height:h, speed:0, type, scale, variant:0,
             asset:getAsset(type,0), spawnScrollTotal:this.background.getScrollTotal(), spawnCanvasY:y };
  }

  spawnHiddenRock(cx: number, cy: number): Obstacle {
    const rock=obstacleSize(this.canvasWidth,false,1.2), k=this.canvasWidth/960;
    const o=this._fixedObs(cx+10*k,cy+50*k,rock.width,rock.height,'rock',rock.shadowScale); this.obstacles.push(o); return o;
  }
  spawnCrashObstacle(cx: number, cy: number): Obstacle {
    const t=Math.random()>0.5,type=t?'tree':'rock' as const,size=obstacleSize(this.canvasWidth,t,1.4),k=this.canvasWidth/960;
    const o=this._fixedObs(cx+25-size.width/2,cy+70*k,size.width,size.height,type,size.shadowScale); this.obstacles.push(o); return o;
  }

  getObstacleType(o: Obstacle): 'tree'|'rock' { return this.obstacles.find(x=>x.id===o.id)?.type??'tree'; }
  getObstacles():  Obstacle[] { return this.obstacles; }
  getCount():      number     { return this.obstacles.length; }

  /** Move existing obstacles with a scrolling background without spawning. */
  followBackground() {
    const scrollNow = this.background.getScrollTotal();
    for (const obs of this.obstacles) obs.y = obs.spawnCanvasY - (scrollNow - obs.spawnScrollTotal);
    this.obstacles = this.obstacles.filter(obs => obs.y > -obs.height - 20);
  }

  clear() { this.obstacles=[]; this.spawnTimer=0; this.spawnSpecIdx=0; }
  pause()  { isPaused=true; }
  resume() { isPaused=false; }

  seekToElapsed(elapsedSeconds: number) { this.reconstituteAtElapsed(elapsedSeconds); }
  seekToSpawn(n: number) { this.spawnSpecIdx=Math.max(0,Math.min(n,this.spawnSpecs.length-1)); this.spawnTimer=0; }
}
